import React from 'react';
import { Session, DayMap, OverridesMap } from '@/types';
import { useHooks } from '@/components/templates/DailyLog/hooks';

export interface DailyLogProps {
  sessions: Session[];
  dayMap: DayMap;
  overrides?: OverridesMap;
  onEditDay?: (date: string) => void;
  onAddManualDay?: () => void;
}

const DailyLog: React.FC<DailyLogProps> = (props) => {
  const { logItems } = useHooks(props);

  if (logItems.length === 0) {
    return (
      <div className="glass-panel p-12 flex flex-col items-center justify-center text-center animate-fade-in delay-100">
        <svg
          className="h-12 w-12 text-foreground/25 mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <h3 className="text-lg font-display font-medium text-foreground/80">
          No Entries Logged
        </h3>
        <p className="text-xs text-foreground/40 font-mono mt-1 uppercase tracking-wider">
          Import timelogs or add manual entries to initialize dashboard data
        </p>
        {props.onAddManualDay && (
          <button
            onClick={props.onAddManualDay}
            className="mt-4 px-4 py-2 bg-accent text-foreground font-mono text-[11px] uppercase tracking-wider rounded font-semibold hover:bg-accent/90 transition-all cursor-pointer"
          >
            Add Manual Entry
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="glass-panel overflow-hidden animate-fade-in delay-100">
      <div className="border-b border-card-border p-4 bg-background/20 flex items-center justify-between">
        <h3 className="text-sm font-mono uppercase tracking-wider text-foreground/60 font-semibold">
          Daily Log Records
        </h3>
        {props.onAddManualDay && (
          <button
            onClick={props.onAddManualDay}
            className="px-3 py-1 bg-accent/10 hover:bg-accent text-accent hover:text-foreground border border-accent/20 rounded text-[10px] font-mono uppercase tracking-wider font-semibold transition-all cursor-pointer"
          >
            Add Manual Entry
          </button>
        )}
      </div>
      <div className="divide-y divide-card-border">
        {logItems.map((item) => (
          <div
            key={item.date}
            className="flex flex-col sm:flex-row sm:items-center justify-between p-4 hover:bg-foreground/[0.01] transition-all gap-4"
          >
            {/* Date & Status Column */}
            <div className="flex items-center gap-2.5 w-44 shrink-0">
              <span className="text-sm font-mono font-medium text-foreground/80">
                {item.date}
              </span>
              {item.status && (
                <span className="bg-accent/10 text-accent border border-accent/20 rounded px-1.5 py-0.5 text-[8px] uppercase font-bold font-mono tracking-wider">
                  {item.status}
                </span>
              )}
            </div>

            {/* Sessions Column */}
            <div className="flex flex-wrap items-center gap-1.5 text-xs text-foreground/50 font-mono flex-grow">
              {item.sessionsList.length > 0 ? (
                item.sessionsList.map((session, idx) => (
                  <React.Fragment key={idx}>
                    {idx > 0 && <span className="text-foreground/20 font-bold mx-1">·</span>}
                    <span className="bg-foreground/[0.03] border border-card-border rounded px-2 py-0.5 text-foreground/75 num-mono">
                      {session.inStr}–{session.outStr}
                    </span>
                  </React.Fragment>
                ))
              ) : (
                <span className="text-foreground/30 italic text-[11px] font-sans">Manual Override / Entry</span>
              )}
            </div>

            {/* Actions & Total Column */}
            <div className="flex items-center gap-4 justify-between sm:justify-end shrink-0 min-w-[9rem]">
              <div className="text-sm font-mono font-semibold text-accent text-right num-mono">
                {item.totalHoursStr}
              </div>
              
              {props.onEditDay && (
                <button
                  onClick={() => props.onEditDay && props.onEditDay(item.date)}
                  className="px-2 py-1 bg-background hover:bg-foreground/5 border border-card-border hover:border-foreground/15 rounded text-[9px] font-mono text-foreground/60 hover:text-foreground transition-all uppercase tracking-wider cursor-pointer"
                >
                  Edit
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DailyLog;

